/**
 * Weapon Unlock Card — lightweight floating notification shown when a special
 * weapon becomes available for the first time during a Battleship game.
 *
 * Appears when an ability transitions locked → available.  Auto-dismisses
 * after 5 seconds.  Offers "Use Now" (activates weapon) and dismiss (×).
 *
 * Animation tier (read from localStorage 'animation_pref'):
 *   none     → no card
 *   reduced  → card appears instantly, fades out
 *   playful  → card slides up with bounce, icon pulses, fades out
 */

import { logger } from '../lib/logger';
import { getAnimationPref, type AnimationTier } from '../lib/animation-pref';

export type AbilityName = 'spread_bomb' | 'sonar_ping' | 'depth_charge';

interface AbilityMeta {
  icon: string;
  title: string;
  description: string;
}

const ABILITY_META: Record<AbilityName, AbilityMeta> = {
  sonar_ping: {
    icon: 'bi-broadcast',
    title: 'Sonar Ping',
    description: 'Scan a 3×3 area — reveals if enemy ships are nearby.',
  },
  spread_bomb: {
    icon: 'bi-bullseye',
    title: 'Spread Bomb',
    description: 'Blast a 3×3 area — hits everything in range!',
  },
  depth_charge: {
    icon: 'bi-water',
    title: 'Depth Charge',
    description: 'Bombard an entire row or column at once.',
  },
};

const AUTO_DISMISS_MS = 5000;
const CARD_ID = 'weaponUnlockCard';

function getAnimationTier(): AnimationTier {
  return getAnimationPref();
}

let _dismissTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Show the weapon-unlock floating card for the given ability.
 *
 * @param ability - The ability that just became available.
 * @param onUseNow - Callback invoked when user clicks "Use Now".
 */
export function showWeaponUnlockCard(
  ability: AbilityName,
  onUseNow: (ability: AbilityName) => void,
): void {
  const tier = getAnimationTier();
  if (tier === 'none') return; // User opted out

  const meta = ABILITY_META[ability];
  if (!meta) {
    logger.warn('[WeaponCard] Unknown ability:', ability);
    return;
  }

  // Remove any existing card
  dismissWeaponUnlockCard();

  const card = document.createElement('div');
  card.id = CARD_ID;
  card.className = `weapon-unlock-card weapon-unlock-card--${tier}`;
  card.setAttribute('role', 'status');
  card.setAttribute('aria-live', 'polite');
  card.innerHTML = `
    <button class="weapon-unlock-card__dismiss" aria-label="Dismiss" data-action="dismiss">
      <i class="bi bi-x" aria-hidden="true"></i>
    </button>
    <div class="weapon-unlock-card__body">
      <div class="weapon-unlock-card__icon">
        <i class="bi ${meta.icon}" aria-hidden="true"></i>
      </div>
      <div class="weapon-unlock-card__text">
        <div class="weapon-unlock-card__title">🔓 ${meta.title} Unlocked!</div>
        <div class="weapon-unlock-card__desc">${meta.description}</div>
      </div>
    </div>
    <div class="weapon-unlock-card__actions">
      <button class="btn btn-sm btn-primary" data-action="use-now">
        Use Now
      </button>
    </div>
  `;

  card.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!target) return;
    const action = target.dataset.action;
    if (action === 'dismiss') {
      dismissWeaponUnlockCard();
    } else if (action === 'use-now') {
      dismissWeaponUnlockCard();
      onUseNow(ability);
    }
  });

  document.body.appendChild(card);

  // Auto-dismiss
  _dismissTimer = setTimeout(() => {
    dismissWeaponUnlockCard();
  }, AUTO_DISMISS_MS);

  logger.info('[WeaponCard] Showing card for', ability, '(tier:', tier + ')');
}

/** Remove the weapon-unlock card from the DOM if it exists. */
export function dismissWeaponUnlockCard(): void {
  if (_dismissTimer !== null) {
    clearTimeout(_dismissTimer);
    _dismissTimer = null;
  }
  const existing = document.getElementById(CARD_ID);
  if (existing) {
    existing.remove();
  }
}
